// src/pages/Tasks/TaskList.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ListChecks, CheckCircle2, AlertTriangle, Plus, ArrowDownUp, ChevronLeft, ChevronRight, Sparkles, Trash2, Layers, User as UserIcon, Users, FileText, FolderInput } from 'lucide-react';
import { tasksApi, Task, TaskFilters } from '../../api/tasks';
import TaskFiltersComponent from '../../components/TaskFilters';
import { useAuth } from '../../contexts/AuthContext';
import TaskCard from '../../components/TaskCard';
import TaskCreate from './TaskCreate';
import { BoardView } from '../../components/BoardView';
import { TaskStats } from '../../components/TaskStats';
import { useAccess } from '../../contexts/AccessContext';
import DraftTasksList, { UnseenBadge, useDraftUnseenTotal } from './DraftTasksList';
import PageHeader from '../../components/PageHeader';
import DeliverTasksDialog from '../../components/DeliverTasksDialog';

type ScopeTab = 'all' | 'mine' | 'team' | 'drafts';

const SCOPE_HEADER: Record<ScopeTab, {
  eyebrow: string;
  title: React.ReactNode;
  subtitle: React.ReactNode;
}> = {
  all: {
    eyebrow: 'ii. Overview',
    title: <>Task <em>Management</em></>,
    subtitle: <>Every task across your <em className="gt-serif-italic">organization</em>, in one feed.</>,
  },
  mine: {
    eyebrow: 'ii. My Work',
    title: <>My <em>Tasks</em></>,
    subtitle: <>Items where you&rsquo;re the <em className="gt-serif-italic">assignee</em> &mdash; your personal queue.</>,
  },
  team: {
    eyebrow: 'ii. Team Pulse',
    title: <>Team <em>Tasks</em></>,
    subtitle: <>What your <em className="gt-serif-italic">team</em> is working on right now.</>,
  },
  drafts: {
    eyebrow: 'ii. Partner Drafts',
    title: <>Draft <em>Tasks</em></>,
    subtitle: <>Incoming requests and outgoing items with your <em className="gt-serif-italic">partners</em>.</>,
  },
};

const CLOSED_STATUSES = new Set(['DONE', 'CANCELLED']);

// Sort tasks by their human-readable code (e.g. TASK-0050) in descending
// order so the most recently created task appears first. The order is
// stable across status / priority / due-date edits. Tasks created before
// the series was configured (code === null) fall to the bottom, ordered
// by id as a tiebreaker.
const sortTasks = (a: Task, b: Task): number => {
  const aHasCode = !!a.code;
  const bHasCode = !!b.code;
  if (aHasCode !== bHasCode) return aHasCode ? -1 : 1;
  if (aHasCode && bHasCode) {
    const cmp = b.code!.localeCompare(a.code!, undefined, { numeric: true, sensitivity: 'base' });
    if (cmp !== 0) return cmp;
  }
  return b.id.localeCompare(a.id);
};

// True when every character of `q` appears in `text`, in order (not necessarily
// contiguous). Lets an abbreviation like "tsk" match "task".
const isSubsequence = (q: string, text: string): boolean => {
  let i = 0;
  for (let j = 0; j < text.length && i < q.length; j++) {
    if (text[j] === q[i]) i += 1;
  }
  return i === q.length;
};

// Mirrors the backend task search: a plain substring match on
// title / description / code, plus a fuzzy subsequence fallback on the short
// title & code fields so dropped-letter abbreviations still hit — e.g. "tsk"
// finds "TASK-0012", "recon" finds "Reconciliation". Subsequence is kept off
// the long description on purpose (it would match almost anything).
const taskMatchesSearch = (t: Task, rawQuery: string): boolean => {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  const title = t.title.toLowerCase();
  const code = (t.code || '').toLowerCase();
  const desc = (t.description || '').toLowerCase();
  if (title.includes(q) || desc.includes(q) || code.includes(q)) return true;
  const compact = q.replace(/\s+/g, '');
  return compact.length >= 2 && (isSubsequence(compact, title) || isSubsequence(compact, code));
};

function TaskList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  // Draft Tasks is a manage-level surface — Access Management decides via the
  // "tasks" panel, no role list.
  const { canManage } = useAccess();
  const isAdmin = canManage('tasks');
  const isLimitedPlan = user?.subscription?.workspace_mode === "limited";
  const currentUserId = user?.id || '';
  const assignedTo = searchParams.get("assigned_to");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [aggregateStatusCounts, setAggregateStatusCounts] = useState<Record<string, number> | undefined>(undefined);
  const [aggregateOverdueCount, setAggregateOverdueCount] = useState<number | undefined>(undefined);
  const [aggregateAllCount, setAggregateAllCount] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Deliver tasks into another project. The dialog is handed the tasks this
  // view currently lists — so the active scope tab and filters decide what is
  // on offer — and does its own search and selection from there.
  const [showDeliver, setShowDeliver] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const initialScope: ScopeTab =
    location.pathname.includes('/my-tasks') ? 'mine' :
      location.pathname.includes('/team-tasks') ? 'team' : 'all';
  const [scope, setScope] = useState<ScopeTab>(initialScope);
  // Combined unseen count for the Draft Tasks scope-tab badge. Polls both
  // partner-draft endpoints every 60s and reacts to in-tab "seen" changes
  // dispatched by DraftTasksList when cards are clicked.
  const draftUnseen = useDraftUnseenTotal(isAdmin && !isLimitedPlan);

  useEffect(() => {
    setScope(
      location.pathname.includes('/my-tasks') ? 'mine' :
        location.pathname.includes('/team-tasks') ? 'team' : 'all'
    );
  }, [location.pathname]);
  const [filters, setFilters] = useState<TaskFilters>({
    page: 1,
    page_size: 9,
    // Sort by task ID (code) descending — newest task first — so the order
    // is stable across status / priority / due-date edits and the latest
    // work surfaces at the top.
    sort_by: 'code',
    sort_dir: 'desc',
    include_closed: false,
    ...(assignedTo && { assigned_to: assignedTo }),
  });

  // Defer the first task fetch until the user's saved filter settings load,
  // so we don't fire the list API twice (once with defaults, once after the
  // saved settings come back).
  const [settingsReady, setSettingsReady] = useState(false);
  const settingsLoadedRef = useRef(false);
  useEffect(() => {
    if (settingsLoadedRef.current) return;
    if (!currentUserId) return;
    settingsLoadedRef.current = true;

    let cancelled = false;
    tasksApi.getTaskSettings()
      .then(() => {
        // Saved task settings are loaded. We intentionally do NOT auto-apply
        // "created by me" here — the list should show all accessible tasks by
        // default. Users can still toggle the "Created by me" checkbox to
        // filter to their own tasks.
      })
      .catch(() => {
        // Defaults already in state — keep going on failure.
        settingsLoadedRef.current = false;
      })
      .finally(() => {
        if (!cancelled) setSettingsReady(true);
      });
    return () => { cancelled = true; };
  }, [currentUserId]);

  useEffect(() => {
    const nextFilters: Partial<TaskFilters> = {};

    const status = searchParams.get("status");
    const overdue = searchParams.get("overdue");
    const dueToday = searchParams.get("due_today");
    const unassigned = searchParams.get("unassigned");
    const assignedToParam = searchParams.get("assigned_to");
    const includeClosed = searchParams.get("include_closed") === "true";

    if (status) nextFilters.status = status;

    if (overdue === "true") {
      nextFilters.overdue = true;
    }

    if (unassigned === "true") {
      nextFilters.assigned_to = "";
    }

    if (assignedToParam) {
      nextFilters.assigned_to = assignedToParam;
    }

    if (includeClosed) {
      nextFilters.include_closed = true;
    }

    if (dueToday === "true") {
      const today = new Date();
      today.setHours(23, 59, 59);

      nextFilters.due_before = today.toISOString();
    }

    setFilters(prev => {
      const updated = {
        ...prev,
        ...nextFilters,
        page: 1,
      };

      // stop unnecessary rerender
      if (JSON.stringify(prev) === JSON.stringify(updated)) {
        return prev;
      }

      return updated;
    });

  }, [searchParams]);


  const loadTasks = useCallback(async () => {
    // Drafts are fetched by DraftTasksList itself (which has its own sub-tabs);
    // skip the regular fetch.
    if (scope === 'drafts') {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const includeClosed = filters.include_closed ?? false;
      let items: Task[] = [];
      let totalCount = 0;
      let nextStatusCounts: Record<string, number> | undefined;
      let nextOverdueCount: number | undefined;
      let nextAllCount: number | undefined;

      if (scope === 'mine') {
        const r = await tasksApi.getMyTasks(includeClosed);
        items = r.data.items || [];
        totalCount = items.length;
      } else if (scope === 'team') {
        const r = await tasksApi.getTeamTasks(includeClosed);
        items = r.data.items || [];
        totalCount = items.length;
      } else {
        // ALL — uses paginated /tasks list with filters
        const finalFilters = {
          ...filters,
          include_closed: includeClosed,
          ...(assignedTo && { assigned_to: assignedTo }),
        };
        const response = await tasksApi.list(finalFilters);
        items = response.data.items || [];
        totalCount = response.data.total || items.length;
        // Server aggregates cover all pages, not just the visible slice.
        nextStatusCounts = response.data.status_counts;
        nextOverdueCount = response.data.overdue_count;
        nextAllCount = response.data.all_count;
      }

      if (scope !== 'all') {
        const tilePool = items.filter(t => {
          if (filters.priority && t.priority !== filters.priority) return false;
          if (filters.search && !taskMatchesSearch(t, filters.search)) return false;
          if (filters.assigned_to && t.primary_assignee_id !== filters.assigned_to) return false;
          return true;
        });
        nextStatusCounts = tilePool.reduce<Record<string, number>>((acc, t) => {
          acc[t.status] = (acc[t.status] || 0) + 1;
          return acc;
        }, {});
        nextOverdueCount = tilePool.filter(t => t.is_overdue && !['DONE', 'CANCELLED'].includes(t.status)).length;
        nextAllCount = tilePool.length;

        if (filters.status) items = items.filter(t => t.status === filters.status);
        if (filters.priority) items = items.filter(t => t.priority === filters.priority);
        if (filters.search) {
          const q = filters.search;
          items = items.filter(t => taskMatchesSearch(t, q));
        }
        if (filters.assigned_to) items = items.filter(t => t.primary_assignee_id === filters.assigned_to);
        if (filters.overdue) items = items.filter(t => t.is_overdue && !['DONE', 'CANCELLED'].includes(t.status));
        totalCount = items.length;
      }

      items = [...items].sort(sortTasks);
      setTasks(items);
      setTotal(totalCount);
      setAggregateStatusCounts(nextStatusCounts);
      setAggregateOverdueCount(nextOverdueCount);
      setAggregateAllCount(nextAllCount);
    } catch (err) {
      console.error("Error loading tasks:", err);
    } finally {
      setLoading(false);
    }
    // }, [filters, assignedTo, scope]);
  }, [
    filters.page,
    filters.page_size,
    filters.sort_by,
    filters.sort_dir,
    filters.status,
    filters.priority,
    filters.search,
    filters.assigned_to,
    filters.overdue,
    filters.include_closed,
    filters.due_before,
    filters.created_by,
    assignedTo,
    scope
  ]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!settingsReady) return;     // wait for saved filter settings
    loadTasks();
  }, [loadTasks, settingsReady]);

  const handleFilterChange = (newFilters: Partial<TaskFilters>) => {
    const merged = { ...filters, ...newFilters, page: 1 };
    if (JSON.stringify(merged) === JSON.stringify(filters)) return;
    setFilters(merged);
  };

  const handleCheckboxSetting = (key: 'include_closed' | 'created_by_me', checked: boolean) => {
    if (key === 'include_closed') {
      handleFilterChange({ include_closed: checked });
    } else {
      handleFilterChange({ created_by: checked && currentUserId ? currentUserId : undefined });
    }

    const includeClosedNext =
      key === 'include_closed' ? checked : (filters.include_closed ?? false);
    const createdByMeNext =
      key === 'created_by_me'
        ? checked
        : !!(filters.created_by && currentUserId && filters.created_by === currentUserId);

    tasksApi.saveTaskSettings({
      include_closed_tasks: includeClosedNext,
      created_by_me: createdByMeNext,
    }).catch(() => { /* non-fatal */ });
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
    window.scrollTo(0, 0);
  };

  const handleStatusClick = (status: string) => {
    if (status === 'ALL') {
      setFilters({ ...filters, status: undefined, overdue: undefined });
    } else if (status === 'OVERDUE') {
      setFilters({ ...filters, status: undefined, overdue: true });
    } else {
      setFilters({ ...filters, status, overdue: undefined });
    }
  };

  const totalPages = Math.ceil(total / filters.page_size!);
  const OPEN_STATUSES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'BLOCKED'];
  const openTasksCount = aggregateStatusCounts
    ? OPEN_STATUSES.reduce((sum, s) => sum + (aggregateStatusCounts[s] || 0), 0)
    : tasks.filter(t => !['DONE', 'CANCELLED'].includes(t.status)).length;
  const overdueCount = aggregateOverdueCount ?? tasks.filter(t => t.is_overdue && !['DONE', 'CANCELLED'].includes(t.status)).length;

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>tasks</em>…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-md text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          <AlertTriangle className="w-9 h-9 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
          <p className="gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>{error}</p>
          <button onClick={loadTasks} className="gt-btn is-danger mt-4 mx-auto">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="gt-shell min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
          {/* Header — eyebrow / title / subtitle swap per active scope tab. */}
          <PageHeader
            eyebrow={SCOPE_HEADER[scope].eyebrow}
            title={SCOPE_HEADER[scope].title}
            subtitle={SCOPE_HEADER[scope].subtitle}
            actions={
              <>
                <button onClick={() => setIsCreateModalOpen(true)} className="gt-btn is-primary is-lg">
                  <Plus className="w-4 h-4" />
                  Create New Task
                </button>
                {tasks.length > 0 && (
                  <button
                    onClick={() => setShowDeliver(true)}
                    className="gt-btn is-ghost"
                    title="Move tasks into another project"
                  >
                    <FolderInput className="w-4 h-4" />
                    <span className="hidden md:inline">Deliver tasks</span>
                  </button>
                )}
                <button
                  onClick={() => navigate('/tasks/recently-deleted')}
                  className="gt-btn is-danger gt-task-trash-btn"
                  aria-label="Recently Deleted"
                  title="Recently Deleted"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden md:inline">Recently Deleted</span>
                </button>
              </>
            }
          />

          {/* Stats Cards — hidden on mobile to keep the list closer to the top */}
          {tasks.length > 0 && (
            <div className="hidden md:grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="gt-stat">
                <div className="gt-stat-label"><ListChecks className="w-3.5 h-3.5" /> Total Tasks</div>
                <p className="gt-stat-value">{total}</p>
              </div>
              <div className="gt-stat">
                <div className="gt-stat-label"><CheckCircle2 className="w-3.5 h-3.5" /> Open Tasks</div>
                <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>{openTasksCount}</p>
              </div>
              <div className="gt-stat" style={{ borderColor: overdueCount > 0 ? 'rgba(220,38,38,0.18)' : undefined }}>
                <div className="gt-stat-label"><AlertTriangle className="w-3.5 h-3.5" /> Overdue</div>
                <p className="gt-stat-value" style={{ color: overdueCount > 0 ? '#991b1b' : undefined }}>{overdueCount}</p>
              </div>
            </div>
          )}

          {/* Scope Tabs — All / My / Team — single-line, horizontally scrollable on narrow screens */}
          <div className="gt-tasks-scope-tabs" style={{ borderBottom: '1px solid var(--gt-border)' }}>
            <nav className="flex gap-1 flex-nowrap overflow-x-auto">
              {[
                { id: 'all' as ScopeTab,    label: 'All Tasks',   icon: <Layers className="w-4 h-4" />,   unseen: 0 },
                { id: 'mine' as ScopeTab,   label: 'My Tasks',    icon: <UserIcon className="w-4 h-4" />, unseen: 0 },
                { id: 'team' as ScopeTab,   label: 'Team Tasks',  icon: <Users className="w-4 h-4" />,    unseen: 0 },
                ...(isAdmin && !isLimitedPlan
                  ? [{ id: 'drafts' as ScopeTab, label: 'Draft Tasks', icon: <FileText className="w-4 h-4" />, unseen: draftUnseen }]
                  : []),
              ].map(tab => {
                const isActive = scope === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setScope(tab.id); setFilters(f => ({ ...f, page: 1 })); }}
                    className={`gt-tasks-scope-tab transition-all${isActive ? ' is-active' : ''}`}
                    style={{
                      marginBottom: -1,
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                    {tab.unseen > 0 && <UnseenBadge count={tab.unseen} />}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Draft Tasks — rendered directly when drafts scope is active.
              DraftTasksList itself hosts the Client/Vendor sub-tabs. */}
          {scope === 'drafts' ? (
            <DraftTasksList />
          ) : (
          <>
          {/* Task Stats */}
          <TaskStats
            tasks={tasks}
            onStatusClick={handleStatusClick}
            statusCounts={aggregateStatusCounts}
            overdueCount={aggregateOverdueCount}
            allCount={aggregateAllCount}
          />

          {/* Filters */}
          <TaskFiltersComponent filters={filters} onFilterChange={handleFilterChange} />

          {/* Tasks Display */}
          {tasks.length === 0 ? (
            <div className="gt-card p-12 text-center">
              <div className="gt-empty">
                <div className="gt-empty-leaf"><Sparkles className="w-6 h-6" /></div>
                <div className="gt-empty-title">No tasks yet</div>
                <div className="gt-page-sub">A clean slate. <em className="gt-serif-italic">Begin</em> with the first one.</div>
                <button onClick={() => setIsCreateModalOpen(true)} className="gt-btn is-primary mt-5 mx-auto">
                  <Plus className="w-4 h-4" />
                  Create your first task
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                {(() => {
                  const pageSize = filters.page_size || 9;
                  const currentPage = filters.page || 1;
                  const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
                  const end = Math.min(currentPage * pageSize, total);
                  return (
                    <p className="gt-page-sub gt-numerals">
                      Showing{' '}
                      <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                        {start}–{end}
                      </span>{' '}
                      of{' '}
                      <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                        {total}
                      </span>{' '}
                      tasks
                    </p>
                  );
                })()}
                <div className="flex items-center gap-4 flex-wrap justify-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.include_closed ?? true}
                      onChange={(e) => handleCheckboxSetting('include_closed', e.target.checked)}
                      style={{ accentColor: 'var(--gt-primary)' }}
                    />
                    <span className="gt-page-sub">Include Closed Tasks</span>
                  </label>

                  <label
                    className="flex items-center gap-2 cursor-pointer"
                    title="Only show tasks I created"
                  >
                    <input
                      type="checkbox"
                      checked={!!(filters.created_by && currentUserId && filters.created_by === currentUserId)}
                      onChange={(e) => handleCheckboxSetting('created_by_me', e.target.checked)}
                      disabled={!currentUserId}
                      style={{ accentColor: 'var(--gt-primary)' }}
                    />
                    <span className="gt-page-sub">Created by me</span>
                  </label>

                  <div className="flex items-center gap-2 gt-page-sub" style={{ background: 'var(--gt-surface-2)', padding: '6px 12px', borderRadius: 999 }}>
                    <ArrowDownUp className="w-3.5 h-3.5" />
                    <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Sorted by Task ID</span>
                  </div>
                </div>
              </div>

              {(() => {
                const pageSize = filters.page_size || 9;
                const currentPage = filters.page || 1;
                const visibleTasks =
                  scope === 'all'
                    ? tasks
                    : tasks.slice((currentPage - 1) * pageSize, currentPage * pageSize);
                return viewMode === 'list' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {visibleTasks.map((task) => (
                      <TaskCard key={task.id} task={task} onRefresh={loadTasks} />
                    ))}
                  </div>
                ) : (
                  <BoardView tasks={visibleTasks} onRefresh={loadTasks} />
                );
              })()}

              {viewMode === 'list' && totalPages > 1 && (
                <>
                  <div className="mt-8 flex justify-center items-center gap-2">
                    <button
                      onClick={() => handlePageChange(filters.page! - 1)}
                      disabled={filters.page === 1}
                      className="gt-btn is-ghost is-sm"
                    >
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>

                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) pageNum = i + 1;
                        else if (filters.page! <= 3) pageNum = i + 1;
                        else if (filters.page! >= totalPages - 2) pageNum = totalPages - 4 + i;
                        else pageNum = filters.page! - 2 + i;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={filters.page === pageNum ? 'gt-btn is-primary is-sm' : 'gt-btn is-ghost is-sm'}
                            style={{ width: 36, height: 36, justifyContent: 'center', padding: 0 }}
                          >
                            <span className="gt-numerals">{pageNum}</span>
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(filters.page! + 1)}
                      disabled={filters.page === totalPages}
                      className="gt-btn is-ghost is-sm"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="text-center gt-page-sub gt-numerals md:hidden">
                    Page {filters.page} of {totalPages}
                  </div>
                </>
              )}
            </>
          )}
          </>
          )}
        </div>
      </div>

      <DeliverTasksDialog
        open={showDeliver}
        onClose={() => setShowDeliver(false)}
        tasks={tasks}
        onDelivered={() => loadTasks()}
      />

      <TaskCreate
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => loadTasks()}
      />
    </>
  );
}

export default TaskList;
